import { useCallback, useRef, useState } from "react";
import type { MutableRefObject } from "react";

export interface UseRowFocusResult {
  focusedRowIndex: number | null;
  setFocusedRowIndex: (index: number | null) => void;
  rowRefs: MutableRefObject<Map<number, HTMLTableRowElement | null>>;
  registerRowRef: (rowIndex: number, el: HTMLTableRowElement | null) => void;
}

/**
 * Tracks which result row is keyboard-focused and keeps a live map of row
 * DOM nodes so the keyboard-shortcut hook can scroll the focused row.
 * Focus moves by keyboard only (see useResultsKeyboardShortcuts).
 */
export const useRowFocus = (): UseRowFocusResult => {
  const [focusedRowIndex, setFocusedRowIndex] = useState<number | null>(null);
  const rowRefs = useRef<Map<number, HTMLTableRowElement | null>>(new Map());

  const registerRowRef = useCallback(
    (rowIndex: number, el: HTMLTableRowElement | null): void => {
      if (el) rowRefs.current.set(rowIndex, el);
      else rowRefs.current.delete(rowIndex);
    },
    [],
  );

  return {
    focusedRowIndex,
    setFocusedRowIndex,
    rowRefs,
    registerRowRef,
  };
};
